import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('gmb_token') || localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('gmb_token');
      localStorage.removeItem('admin_token');
      if (!window.location.pathname.startsWith('/gmbadmin')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Tools APIs
export const toolsAPI = {
  generatePosts: (data) => api.post('/tools/generate-posts', data),
  generateReplies: (data) => api.post('/tools/generate-replies', data),
  runAudit: (data) => api.post('/tools/audit', data),
  generateImage: (data) => api.post('/tools/generate-image', data),
};

// Posts APIs
export const postsAPI = {
  schedule: (data) => api.post('/posts/schedule', data),
  getScheduled: () => api.get('/posts/scheduled'),
  delete: (postId) => api.delete(`/posts/${postId}`),
  publish: (postId) => api.put(`/posts/${postId}/publish`),
};

// Replies APIs
export const repliesAPI = {
  save: (data) => api.post('/replies/save', data),
  getSaved: () => api.get('/replies/saved'),
  approve: (replyId) => api.put(`/replies/${replyId}/approve`),
};

// Auto-Post APIs
export const autoPostAPI = {
  getSettings: () => api.get('/auto-post/settings'),
  updateSettings: (data) => api.post('/auto-post/settings', data),
  generateNow: (postType) => api.post('/auto-post/generate-now', { post_type: postType }),
};

// Auto-Reply APIs
export const autoReplyAPI = {
  getSettings: () => api.get('/auto-reply/settings'),
  updateSettings: (data) => api.post('/auto-reply/settings', data),
  generateNow: () => api.post('/auto-reply/generate-now'),
};

// Google Business Profile APIs
export const gmbAPI = {
  connect: () => api.get('/gmb/connect'),
  status: () => api.get('/gmb/status'),
  locations: () => api.get('/gmb/locations'),
  publish: (data) => api.post('/gmb/publish', data),
  disconnect: () => api.post('/gmb/disconnect'),
};

// Payment APIs
export const paymentAPI = {
  getConfig: () => api.get('/payments/config'),
  createCheckout: (plan, paymentMethod, originUrl) => 
    api.post('/payments/create-checkout', { plan, payment_method: paymentMethod, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  verifyRazorpay: (orderId, paymentId, signature) => 
    api.post('/payments/razorpay/verify', { order_id: orderId, payment_id: paymentId, signature }),
};

// Analytics APIs
export const analyticsAPI = {
  getUsage: () => api.get('/analytics/usage'),
  getHistory: (tool) => api.get('/analytics/history', { params: { tool } }),
};

// Plans APIs
export const plansAPI = {
  getAll: () => api.get('/plans'),
};

// Admin APIs
export const adminAPI = {
  login: (email, password) => api.post('/admin/login', { email, password }),
  getStats: () => api.get('/admin/stats'),
  getUsers: (page = 1, limit = 20) => api.get('/admin/users', { params: { page, limit } }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getMembershipPlans: () => api.get('/admin/membership-plans'),
  updateMembershipPlans: (plans) => api.put('/admin/membership-plans', plans),
  getPosts: (page = 1, limit = 20, status = null) => 
    api.get('/admin/posts', { params: { page, limit, status } }),
  getTransactions: (page = 1, params = {}) => 
    api.get('/admin/transactions', { params: { page, limit: 20, ...params } }),
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSiteSettings: (data) => api.put('/admin/settings/site', data),
  getSmtpSettings: () => api.get('/admin/settings/smtp'),
  updateSmtpSettings: (data) => api.put('/admin/settings/smtp', data),
  getPaymentGateway: () => api.get('/admin/settings/payment-gateway'),
  updatePaymentGateway: (data) => api.put('/admin/settings/payment-gateway', data),
  changePassword: (data) => api.post('/admin/change-password', data),
  updateProfile: (data) => api.put('/admin/profile', data),
  getSiteSettings: () => api.get('/admin/settings/site'),
  // Template Management
  getTemplates: () => api.get('/admin/templates'),
  updateTemplate: (templateId, data) => api.put(`/admin/templates/${templateId}`, data),
};

// Site Status (public)
export const siteAPI = {
  getStatus: () => api.get('/site/status'),
};

// Password APIs
export const passwordAPI = {
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Creative Template APIs
export const templateAPI = {
  getAll: () => api.get('/templates'),
  select: (templateIds) => api.post('/templates/select', templateIds),
  getSelected: () => api.get('/templates/selected'),
  generate: (data) => api.post('/templates/generate', data),
};

// User Branding APIs
export const brandingAPI = {
  get: () => api.get('/user/branding'),
  update: (data) => api.put('/user/branding', data),
};

// Team Collaboration APIs
export const teamAPI = {
  getMembers: () => api.get('/team/members'),
  invite: (data) => api.post('/team/invite', data),
  removeMember: (memberId) => api.delete(`/team/members/${memberId}`),
  updateMember: (memberId, data) => api.put(`/team/members/${memberId}`, data),
  cancelInvitation: (invitationId) => api.delete(`/team/invitations/${invitationId}`),
  validateInvite: (token) => api.get(`/team/invite/${token}`),
  acceptInvite: (token) => api.post(`/team/invite/${token}/accept`),
  getMyTeams: () => api.get('/team/my-teams'),
};

// Referral APIs
export const referralAPI = {
  getLink: () => api.get('/referral/link'),
  getDashboard: () => api.get('/referral/dashboard'),
  getReferrals: () => api.get('/referral/referrals'),
  getCommissions: () => api.get('/referral/commissions'),
  getWallet: () => api.get('/referral/wallet'),
  requestPayout: (data) => api.post('/referral/payout/request', data),
  getPayoutSettings: () => api.get('/referral/payout-settings'),
  updatePayoutSettings: (data) => api.put('/referral/payout-settings', data),
  trackClick: (code) => api.get(`/referral/track/${code}`),
  getLeaderboard: () => api.get('/referral/leaderboard'),
};

// Admin Referral APIs
export const adminReferralAPI = {
  getSettings: () => api.get('/admin/referral/settings'),
  updateSettings: (data) => api.put('/admin/referral/settings', data),
  getOverview: () => api.get('/admin/referral/overview'),
  getCommissions: (page = 1, limit = 20, status = null) =>
    api.get('/admin/referral/commissions', { params: { page, limit, status } }),
  approveCommission: (id) => api.post(`/admin/referral/commissions/${id}/approve`),
  getReferrals: (page = 1, limit = 20) =>
    api.get('/admin/referral/referrals', { params: { page, limit } }),
  getPayouts: (page = 1, limit = 20, status = null) =>
    api.get('/admin/referral/payouts', { params: { page, limit, status } }),
  processPayout: (id) => api.post(`/admin/referral/payouts/${id}/process`),
  rejectPayout: (id) => api.post(`/admin/referral/payouts/${id}/reject`),
};

// Admin Email Template APIs
export const adminEmailAPI = {
  getAll: () => api.get('/admin/email-templates'),
  get: (key) => api.get(`/admin/email-templates/${key}`),
  update: (key, data) => api.put(`/admin/email-templates/${key}`, data),
  create: (data) => api.post('/admin/email-templates', data),
  delete: (key) => api.delete(`/admin/email-templates/${key}`),
  toggle: (key) => api.post(`/admin/email-templates/${key}/toggle`),
  test: (key) => api.post(`/admin/email-templates/${key}/test`),
};

// Multi-Location APIs (GMB-connected)
export const locationsAPI = {
  getAll: () => api.get('/locations'),
  activate: (gmbLocationId) => api.post(`/locations/${encodeURIComponent(gmbLocationId)}/activate`),
  deactivate: (gmbLocationId) => api.post(`/locations/${encodeURIComponent(gmbLocationId)}/deactivate`),
  setPrimary: (gmbLocationId) => api.put(`/locations/${encodeURIComponent(gmbLocationId)}/primary`),
  refresh: () => api.post('/locations/refresh'),
};

// Analytics APIs
export const dashboardAnalyticsAPI = {
  get: (dateRange = '1m', startDate, endDate) => {
    const params = { date_range: dateRange };
    if (dateRange === 'custom' && startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    }
    return api.get('/analytics/dashboard', { params });
  },
};

// CMS Pages APIs (public)
export const pagesAPI = {
  getPage: (slug) => api.get(`/pages/${slug}`),
  listPages: () => api.get('/pages'),
};

// Billing APIs
export const billingAPI = {
  get: () => api.get('/billing'),
  downloadInvoice: (transactionId) => api.get(`/billing/invoice/${transactionId}`, { responseType: 'blob' }),
};

// Admin CMS Pages APIs
export const adminPagesAPI = {
  getAll: () => api.get('/admin/pages'),
  create: (data) => api.post('/admin/pages', data),
  update: (pageId, data) => api.put(`/admin/pages/${pageId}`, data),
  delete: (pageId) => api.delete(`/admin/pages/${pageId}`),
  toggle: (pageId) => api.post(`/admin/pages/${pageId}/toggle`),
};

// FAQ APIs (public)
export const faqAPI = {
  getAll: () => api.get('/faqs'),
};

// Admin FAQ APIs
export const adminFaqAPI = {
  getAll: () => api.get('/admin/faqs'),
  create: (data) => api.post('/admin/faqs', data),
  update: (faqId, data) => api.put(`/admin/faqs/${faqId}`, data),
  delete: (faqId) => api.delete(`/admin/faqs/${faqId}`),
  toggle: (faqId) => api.post(`/admin/faqs/${faqId}/toggle`),
};

export default api;
